/**
 * Media kit section library (fixed set of 8).
 * Per-kit layout only enables / disables / reorders these types — no freeform sections.
 */

export type MediaKitSectionType =
  | "hero"
  | "about"
  | "performance"
  | "audience"
  | "visualWorld"
  | "campaigns"
  | "collaboration"
  | "contact";

export type MediaKitLayoutSection = {
  type: MediaKitSectionType;
  enabled: boolean;
  /** Lower = earlier on the public deck */
  order: number;
  /** Optional nav label override (public pills) */
  navLabel?: string;
};

export type MediaKitLayout = {
  sections: MediaKitLayoutSection[];
};

/** DOM id on public SPA (hash / spy / aria) */
export const SECTION_PUBLIC_ID: Record<MediaKitSectionType, string> = {
  hero: "portada",
  about: "perfil",
  performance: "metricas",
  audience: "audiencia",
  visualWorld: "mundo",
  campaigns: "conceptos",
  collaboration: "formatos",
  contact: "contacto",
};

/** Default brand vocabulary for nav pills */
export const SECTION_DEFAULT_NAV_LABEL: Record<MediaKitSectionType, string> = {
  hero: "Cover",
  about: "Sobre",
  performance: "Stats",
  audience: "Audiencia",
  visualWorld: "Portfolio",
  campaigns: "Ejemplos",
  collaboration: "Servicios",
  contact: "Contacto",
};

/** Admin document rail labels */
export const SECTION_ADMIN_LABEL: Record<MediaKitSectionType, string> = {
  hero: "Cover",
  about: "Sobre",
  performance: "Stats",
  audience: "Audiencia",
  visualWorld: "Portfolio",
  campaigns: "Ejemplos",
  collaboration: "Servicios",
  contact: "Contacto",
};

export const SECTION_ADMIN_HINT: Record<MediaKitSectionType, string> = {
  hero: "Nombre, bio, redes, marca",
  about: "Por qué contratarla",
  performance: "Engagement, vistas, capacidad",
  audience: "Edad, países, género",
  visualWorld: "Fotos + títulos de la sección visual",
  campaigns: "Conceptos de contenido",
  collaboration: "Qué puede contratar la marca",
  contact: "CTA y brief",
};

export const ALL_SECTION_TYPES: MediaKitSectionType[] = [
  "hero",
  "about",
  "performance",
  "audience",
  "visualWorld",
  "campaigns",
  "collaboration",
  "contact",
];

export function defaultLayout(): MediaKitLayout {
  return {
    sections: ALL_SECTION_TYPES.map((type, i) => ({
      type,
      enabled: true,
      order: (i + 1) * 10,
      navLabel: SECTION_DEFAULT_NAV_LABEL[type],
    })),
  };
}

const VALID = new Set<string>(ALL_SECTION_TYPES);

/**
 * Normalize layout: known types only, unique, fill missing as disabled at end,
 * stable order by `order` then library order.
 */
export function normalizeLayout(
  partial?: MediaKitLayout | null,
): MediaKitLayout {
  const base = defaultLayout();
  if (!partial?.sections || !Array.isArray(partial.sections)) {
    return base;
  }

  const seen = new Set<MediaKitSectionType>();
  const fromCms: MediaKitLayoutSection[] = [];

  for (const raw of partial.sections) {
    if (!raw || typeof raw !== "object") continue;
    const type = (raw as MediaKitLayoutSection).type;
    if (!VALID.has(type) || seen.has(type)) continue;
    seen.add(type);
    const libOrder = ALL_SECTION_TYPES.indexOf(type);
    fromCms.push({
      type,
      enabled: (raw as MediaKitLayoutSection).enabled !== false,
      order:
        typeof (raw as MediaKitLayoutSection).order === "number"
          ? (raw as MediaKitLayoutSection).order
          : (libOrder + 1) * 10,
      navLabel:
        typeof (raw as MediaKitLayoutSection).navLabel === "string" &&
        (raw as MediaKitLayoutSection).navLabel!.trim()
          ? (raw as MediaKitLayoutSection).navLabel!.trim()
          : SECTION_DEFAULT_NAV_LABEL[type],
    });
  }

  for (const type of ALL_SECTION_TYPES) {
    if (seen.has(type)) continue;
    fromCms.push({
      type,
      enabled: false,
      order: 1000 + ALL_SECTION_TYPES.indexOf(type) * 10,
      navLabel: SECTION_DEFAULT_NAV_LABEL[type],
    });
  }

  fromCms.sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return ALL_SECTION_TYPES.indexOf(a.type) - ALL_SECTION_TYPES.indexOf(b.type);
  });

  // Re-stamp sequential order for clean saves
  return {
    sections: fromCms.map((s, i) => ({ ...s, order: (i + 1) * 10 })),
  };
}

export type PublicNavItem = {
  type: MediaKitSectionType;
  id: string;
  label: string;
};

/** Enabled sections in display order for public nav + render. */
export function resolvePublicNav(
  layout?: MediaKitLayout | null,
): PublicNavItem[] {
  const { sections } = normalizeLayout(layout);
  return sections
    .filter((s) => s.enabled)
    .map((s) => ({
      type: s.type,
      id: SECTION_PUBLIC_ID[s.type],
      label: s.navLabel?.trim() || SECTION_DEFAULT_NAV_LABEL[s.type],
    }));
}

/** Public path for a kit: general → /mediakit, else /mediakit/{slug} */
export function mediaKitPublicPath(
  slug: string,
  isGeneral?: boolean,
): string {
  if (isGeneral) return "/mediakit";
  const s = slug?.trim();
  if (!s || s === "delfu-ocampo" || s === "general") return "/mediakit";
  return `/mediakit/${encodeURIComponent(s)}`;
}

/** Slugify for vertical kits: "Fashion Beauty" → "fashion-beauty" */
export function slugifyMediaKit(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}
